import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { RootState } from "../../store/store";
import { Typography } from "@material-tailwind/react";
import moment from "moment";
import { toast } from "react-toastify";
import debounce from "lodash/debounce";
import {
  AllPatientList,
  UpdatePatientInfo,
  AdminSetPassword,
  AdminSendResetEmail,
  AdminEnrollPatient,
} from "../../store/PatientSlice";
import { PatientList, PatientBooking } from "../../interfaces/Patients";
import apiClient from "../../api/apiClient";
import Modal from "../Modal";

interface ProgramOption {
  id: string;
  title: string;
  cost?: number;
}

const PatientsView = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();

  // State
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 10;

  // Available programs for enrollment
  const [programsList, setProgramsList] = useState<ProgramOption[]>([]);
  const [programsLoading, setProgramsLoading] = useState(false);

  // Modals state
  const [editModalPatient, setEditModalPatient] = useState<PatientList | null>(null);
  const [editForm, setEditForm] = useState({ fullName: "", email: "", phoneNo: "" });

  const [passwordModalPatient, setPasswordModalPatient] = useState<PatientList | null>(null);
  const [passwordTab, setPasswordTab] = useState<"email" | "manual">("email");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [enrollModalPatient, setEnrollModalPatient] = useState<PatientList | null>(null);
  const [selectedProgramId, setSelectedProgramId] = useState("");

  const [bookingsModalPatient, setBookingsModalPatient] = useState<PatientList | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redux Selectors
  const patientState = useSelector((state: RootState) => state.patient);
  const patientAll = patientState.data || ([] as PatientList[]);
  const total = patientState.total || 0;
  const totalPages = patientState.totalPages || 1;
  const loading = patientState.loading;

  // Debounced search handler (400ms)
  const debouncedSearchHandler = useRef(
    debounce((query: string) => {
      setDebouncedSearch(query);
      setCurrentPage(1);
    }, 400)
  ).current;

  useEffect(() => {
    return () => {
      debouncedSearchHandler.cancel();
    };
  }, [debouncedSearchHandler]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearch(val);
    debouncedSearchHandler(val);
  };

  const handleClearSearch = () => {
    setSearch("");
    setDebouncedSearch("");
    debouncedSearchHandler.cancel();
    setCurrentPage(1);
  };

  // Fetch patients when page or debounced search changes
  const fetchPatients = useCallback(() => {
    dispatch(AllPatientList({ page: currentPage, limit, search: debouncedSearch }));
  }, [dispatch, currentPage, limit, debouncedSearch]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  // Fetch available programs for enrollment modal
  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        setProgramsLoading(true);
        const res = await apiClient.get("/api/v1/programs?limit=100");
        const list = res.data?.data?.program || res.data?.data?.programs || [];
        setProgramsList(list);
      } catch (err) {
        console.error("Failed to load programs list for enrollment", err);
      } finally {
        setProgramsLoading(false);
      }
    };
    fetchPrograms();
  }, []);

  // Handlers for Modals
  const openEditModal = (patient: PatientList) => {
    setEditModalPatient(patient);
    setEditForm({
      fullName: patient.fullName || "",
      email: patient.email || "",
      phoneNo: patient.phoneNo || "",
    });
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editModalPatient) return;
    if (!editForm.fullName.trim() || !editForm.email.trim()) {
      toast.error("Full Name and Email are required");
      return;
    }

    try {
      setIsSubmitting(true);
      await dispatch(
        UpdatePatientInfo({
          id: editModalPatient.id,
          fullName: editForm.fullName.trim(),
          email: editForm.email.trim(),
          phoneNo: editForm.phoneNo.trim(),
        })
      ).unwrap();

      toast.success(t("adminPatients.alerts.updateSuccess", "Patient information updated successfully!"));
      setEditModalPatient(null);
      fetchPatients();
    } catch (error: any) {
      toast.error(error || t("adminPatients.alerts.updateError", "Failed to update patient information"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const openPasswordModal = (patient: PatientList) => {
    setPasswordModalPatient(patient);
    setPasswordTab("email");
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleSendResetEmail = async () => {
    if (!passwordModalPatient) return;
    try {
      setIsSubmitting(true);
      await dispatch(AdminSendResetEmail(passwordModalPatient.id)).unwrap();
      toast.success(t("adminPatients.alerts.passwordSuccess", `Password reset email sent to ${passwordModalPatient.email}!`));
      setPasswordModalPatient(null);
    } catch (error: any) {
      toast.error(error || t("adminPatients.alerts.passwordError", "Failed to send reset email"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleManualSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwordModalPatient) return;

    if (newPassword.length < 8) {
      toast.error(t("authValidation.passwordMinLength", "Password must be at least 8 characters long"));
      return;
    }
    if (!/[\W_]/.test(newPassword)) {
      toast.error(t("authValidation.passwordSpecialChar", "Password must contain at least 1 special character"));
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error(t("adminPatients.alerts.passwordMismatch", "Passwords do not match"));
      return;
    }

    try {
      setIsSubmitting(true);
      await dispatch(
        AdminSetPassword({
          id: passwordModalPatient.id,
          password: newPassword,
        })
      ).unwrap();

      toast.success(t("adminPatients.alerts.passwordSuccess", "Password updated successfully!"));
      setPasswordModalPatient(null);
    } catch (error: any) {
      toast.error(error || t("adminPatients.alerts.passwordError", "Failed to set password"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEnrollModal = (patient: PatientList) => {
    setEnrollModalPatient(patient);
    setSelectedProgramId("");
  };

  const handleEnrollSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!enrollModalPatient) return;
    if (!selectedProgramId) {
      toast.error(t("adminPatients.alerts.selectProgramReq", "Please select a program to enroll"));
      return;
    }

    try {
      setIsSubmitting(true);
      await dispatch(
        AdminEnrollPatient({
          patientId: enrollModalPatient.id,
          programId: selectedProgramId,
        })
      ).unwrap();

      toast.success(t("adminPatients.alerts.enrollSuccess", "Patient enrolled successfully with free access!"));
      setEnrollModalPatient(null);
      fetchPatients();
    } catch (error: any) {
      toast.error(error || t("adminPatients.alerts.enrollError", "Failed to enroll patient in program"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const openBookingsModal = (patient: PatientList) => {
    setBookingsModalPatient(patient);
  };

  const TABLE_HEAD = [
    t("adminPatients.table.patients", "#"),
    t("adminPatients.table.patientName", "PATIENT / CONTACT"),
    t("adminPatients.table.phone", "PHONE"),
    t("adminPatients.table.programBooked", "PROGRAMS ENROLLED"),
    t("adminPatients.table.dateRegistered", "DATE REGISTERED"),
    t("adminPatients.table.actions", "ACTIONS"),
  ];

  return (
    <div className="w-full h-full flex flex-col overflow-hidden bg-[#FAFBFC]">
      {/* Search & Stats Toolbar */}
      <div className="p-4 bg-white border-b border-gray-100 flex flex-wrap items-center justify-between gap-4">
        <div className="relative w-80">
          <input
            type="text"
            value={search}
            onChange={handleSearchChange}
            placeholder={t("adminPatients.searchPlaceholder", "Search by name, email, or phone...")}
            className="w-full border border-gray-300 rounded-lg pl-9 pr-8 py-2 text-sm focus:outline-none focus:border-[#B4804A] transition-colors"
          />
          <svg
            className="w-4 h-4 text-gray-400 absolute left-3 top-2.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {search && (
            <button
              onClick={handleClearSearch}
              className="absolute right-2.5 top-2 text-gray-400 hover:text-gray-600 text-sm cursor-pointer"
            >
              ✕
            </button>
          )}
        </div>

        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 font-medium">
            {t("adminPatients.totalCount", "Total Patients:")}{" "}
            <strong className="text-gray-800 text-base">{total}</strong>
          </span>
        </div>
      </div>

      {/* Table Content */}
      <div className="w-full flex-1 overflow-x-auto overflow-y-auto">
        <table className="w-full min-w-[850px] table-auto text-left">
          <thead className="sticky top-0 bg-white z-10 shadow-xs">
            <tr className="bg-[#F8F9FA] text-left border-b-2 border-gray-100">
              {TABLE_HEAD.map((head, i) => (
                <th
                  key={i}
                  className="py-3.5 px-6 font-bold text-[#7E8299] uppercase text-xs whitespace-nowrap"
                >
                  <Typography
                    variant="small"
                    className="font-semibold leading-none text-[#7E8299] opacity-70 whitespace-nowrap"
                  >
                    {head}
                  </Typography>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {loading ? (
              <tr>
                <td colSpan={6} className="text-center py-16">
                  <div className="flex flex-col items-center justify-center gap-3">
                    <div className="w-7 h-7 border-3 border-[#B4804A] border-t-transparent rounded-full animate-spin" />
                    <span className="text-sm text-gray-500 font-medium">Loading patients...</span>
                  </div>
                </td>
              </tr>
            ) : !patientAll || patientAll.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-16 text-gray-500 font-medium">
                  <div className="max-w-sm mx-auto flex flex-col items-center gap-2">
                    <svg className="w-10 h-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <p className="text-gray-600 font-semibold">{t("adminPatients.noPatients", "No patients found")}</p>
                    {search && (
                      <p className="text-xs text-gray-400">
                        No matches found for &quot;{search}&quot;. Try adjusting your search query.
                      </p>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              patientAll.map((patient, index) => {
                const count = patient.totalBookings ?? patient.bookings?.length ?? 0;
                return (
                  <tr key={patient.id || index} className="hover:bg-amber-50/20 transition-colors">
                    <td className="py-4 px-6 whitespace-nowrap text-xs text-gray-400 font-medium">
                      {(currentPage - 1) * limit + index + 1}
                    </td>

                    {/* Patient / Contact info */}
                    <td className="py-4 px-6 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="font-semibold text-gray-900 text-sm">{patient?.fullName || "—"}</span>
                        <a
                          href={`mailto:${patient?.email}`}
                          className="text-xs text-[#B4804A] hover:underline flex items-center gap-1 mt-0.5"
                        >
                          <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          {patient?.email || "—"}
                        </a>
                      </div>
                    </td>

                    {/* Phone */}
                    <td className="py-4 px-6 whitespace-nowrap text-sm text-gray-600">
                      {patient?.phoneNo ? (
                        <span className="font-mono text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                          {patient.phoneNo}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>

                    {/* Programs Enrolled */}
                    <td className="py-4 px-6 whitespace-nowrap">
                      <button
                        onClick={() => openBookingsModal(patient)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                          count > 0
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                            : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                        }`}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-current" />
                        {count} {count === 1 ? "Program" : "Programs"}
                      </button>
                    </td>

                    {/* Date Registered */}
                    <td className="py-4 px-6 whitespace-nowrap text-sm text-gray-500">
                      {patient.createdAt ? moment(patient.createdAt).format("DD MMM, YYYY") : "—"}
                    </td>

                    {/* Actions */}
                    <td className="py-4 px-6 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        {/* View Details */}
                        <button
                          onClick={() => openBookingsModal(patient)}
                          title={t("adminPatients.viewRegistrations", "View Registrations")}
                          className="p-1.5 text-gray-500 hover:text-[#041B44] hover:bg-gray-100 rounded-md transition-colors cursor-pointer"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>

                        {/* Edit Info */}
                        <button
                          onClick={() => openEditModal(patient)}
                          title={t("adminPatients.editInfo", "Edit Contact Info")}
                          className="p-1.5 text-gray-500 hover:text-amber-700 hover:bg-amber-50 rounded-md transition-colors cursor-pointer"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>

                        {/* Password / Reset */}
                        <button
                          onClick={() => openPasswordModal(patient)}
                          title={t("adminPatients.setPassword", "Set / Reset Password")}
                          className="p-1.5 text-gray-500 hover:text-indigo-700 hover:bg-indigo-50 rounded-md transition-colors cursor-pointer"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                          </svg>
                        </button>

                        {/* Grant Free Access */}
                        <button
                          onClick={() => openEnrollModal(patient)}
                          title={t("adminPatients.grantFreeAccess", "Grant Free Access")}
                          className="flex items-center gap-1 px-2.5 py-1 bg-[#B4804A]/10 text-[#B4804A] hover:bg-[#B4804A] hover:text-white rounded-md text-xs font-semibold transition-colors cursor-pointer ml-1"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          <span>{t("adminPatients.grantFreeAccess", "Free Access")}</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      {totalPages > 1 && (
        <div className="p-4 bg-white border-t border-gray-100 flex items-center justify-between">
          <span className="text-xs text-gray-500 font-medium">
            Page {currentPage} of {totalPages}
          </span>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 border border-gray-300 rounded text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
              .map((p, idx, arr) => (
                <React.Fragment key={p}>
                  {idx > 0 && arr[idx - 1] !== p - 1 && <span className="px-1 text-gray-400 text-xs">...</span>}
                  <button
                    onClick={() => setCurrentPage(p)}
                    className={`w-7 h-7 rounded text-xs font-semibold cursor-pointer ${
                      currentPage === p
                        ? "bg-[#041B44] text-white"
                        : "border border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {p}
                  </button>
                </React.Fragment>
              ))}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 border border-gray-300 rounded text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* 1. EDIT PATIENT MODAL */}
      {editModalPatient && (
        <Modal
          isOpen={true}
          onClose={() => setEditModalPatient(null)}
          title={t("adminPatients.modal.editTitle", "Edit Patient Information")}
          subtitle={t("adminPatients.modal.editSubtitle", "Update contact details and personal information.")}
          size="md"
        >
          <form onSubmit={handleSaveEdit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                {t("adminPatients.modal.fullName", "Full Name")} *
              </label>
              <input
                type="text"
                required
                value={editForm.fullName}
                onChange={(e) => setEditForm({ ...editForm, fullName: e.target.value })}
                placeholder={t("adminPatients.modal.fullNamePlaceholder", "Enter full name")}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#B4804A]"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                {t("adminPatients.modal.email", "Email Address")} *
              </label>
              <input
                type="email"
                required
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                placeholder={t("adminPatients.modal.emailPlaceholder", "Enter email address")}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#B4804A]"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                {t("adminPatients.modal.phone", "Phone Number")}
              </label>
              <input
                type="tel"
                value={editForm.phoneNo}
                onChange={(e) => setEditForm({ ...editForm, phoneNo: e.target.value })}
                placeholder={t("adminPatients.modal.phonePlaceholder", "e.g. +1 555 123 4567")}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#B4804A]"
              />
            </div>

            <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2.5 sm:gap-3 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setEditModalPatient(null)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer text-center"
              >
                {t("adminPatients.modal.cancel", "Cancel")}
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-5 py-2 bg-[#B4804A] hover:bg-[#9d6c38] text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 cursor-pointer text-center"
              >
                {isSubmitting ? t("adminPatients.modal.saving", "Saving...") : t("adminPatients.modal.save", "Save Changes")}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* 2. PASSWORD MANAGEMENT MODAL (EMAIL RESET vs MANUAL SET) */}
      {passwordModalPatient && (
        <Modal
          isOpen={true}
          onClose={() => setPasswordModalPatient(null)}
          title={t("adminPatients.modal.passwordTitle", "Set / Reset Password")}
          subtitle={`For: ${passwordModalPatient.fullName} (${passwordModalPatient.email})`}
          size="md"
        >
          {/* Tab Selector */}
          <div className="flex border-b border-gray-200 bg-gray-50 -mx-3.5 sm:-mx-6 -mt-3.5 sm:-mt-6 mb-4">
            <button
              type="button"
              onClick={() => setPasswordTab("email")}
              className={`flex-1 py-3 text-xs font-semibold text-center border-b-2 transition-colors cursor-pointer flex items-center justify-center gap-1.5 ${
                passwordTab === "email"
                  ? "border-[#B4804A] text-[#B4804A] bg-white"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Send Reset Email
            </button>
            <button
              type="button"
              onClick={() => setPasswordTab("manual")}
              className={`flex-1 py-3 text-xs font-semibold text-center border-b-2 transition-colors cursor-pointer flex items-center justify-center gap-1.5 ${
                passwordTab === "manual"
                  ? "border-[#B4804A] text-[#B4804A] bg-white"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
              Manually Set Password
            </button>
          </div>

          <div>
            {passwordTab === "email" ? (
              <div className="space-y-4 text-center">
                <div className="w-12 h-12 bg-amber-50 text-[#B4804A] rounded-full flex items-center justify-center mx-auto">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 text-sm">Send Password Reset Link</h4>
                  <p className="text-xs text-gray-500 mt-1">
                    A secure, personalized password reset link will be sent to{" "}
                    <strong className="text-gray-700">{passwordModalPatient.email}</strong>.
                  </p>
                </div>

                <div className="pt-2 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2.5 sm:gap-3">
                  <button
                    type="button"
                    onClick={() => setPasswordModalPatient(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer text-center"
                  >
                    {t("adminPatients.modal.cancel", "Cancel")}
                  </button>
                  <button
                    type="button"
                    onClick={handleSendResetEmail}
                    disabled={isSubmitting}
                    className="px-5 py-2 bg-[#041B44] hover:bg-[#031535] text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 cursor-pointer text-center"
                  >
                    {isSubmitting ? "Sending..." : "Send Reset Link"}
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleManualSetPassword} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    {t("adminPatients.modal.newPassword", "New Password")} *
                  </label>
                  <input
                    type="password"
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={t("adminPatients.modal.newPasswordPlaceholder", "Min. 8 chars with 1 special char")}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#B4804A]"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    {t("adminPatients.modal.confirmPassword", "Confirm Password")} *
                  </label>
                  <input
                    type="password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t("adminPatients.modal.confirmPasswordPlaceholder", "Repeat new password")}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#B4804A]"
                  />
                </div>

                <p className="text-[11px] text-gray-400">
                  Must be 8-20 characters long with at least 1 special character (!@#$%^&*...).
                </p>

                <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2.5 sm:gap-3 pt-4 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setPasswordModalPatient(null)}
                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer text-center"
                  >
                    {t("adminPatients.modal.cancel", "Cancel")}
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-5 py-2 bg-[#B4804A] hover:bg-[#9d6c38] text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 cursor-pointer text-center"
                  >
                    {isSubmitting
                      ? t("adminPatients.modal.settingPassword", "Setting...")
                      : t("adminPatients.modal.setPasswordBtn", "Set New Password")}
                  </button>
                </div>
              </form>
            )}
          </div>
        </Modal>
      )}

      {/* 3. GRANT FREE ACCESS / PROGRAM ENROLLMENT MODAL */}
      {enrollModalPatient && (
        <Modal
          isOpen={true}
          onClose={() => setEnrollModalPatient(null)}
          title={t("adminPatients.modal.enrollTitle", "Grant Free Program Access")}
          subtitle={`Enrolling: ${enrollModalPatient.fullName}`}
          size="md"
        >
          <form onSubmit={handleEnrollSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                {t("adminPatients.modal.selectProgram", "Select Program")} *
              </label>
              {programsLoading ? (
                <div className="py-2 text-xs text-gray-500">Loading available programs...</div>
              ) : (
                <select
                  required
                  value={selectedProgramId}
                  onChange={(e) => setSelectedProgramId(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#B4804A] bg-white cursor-pointer"
                >
                  <option value="">{t("adminPatients.modal.chooseProgram", "-- Choose a Program --")}</option>
                  {programsList.map((prog) => (
                    <option key={prog.id} value={prog.id}>
                      {prog.title} {prog.cost ? `($${prog.cost})` : "(Free)"}
                    </option>
                  ))}
                </select>
              )}
              <p className="text-[11px] text-gray-500 mt-1.5">
                The patient will be immediately granted full access to this program with an amount of $0 (Free Access).
              </p>
            </div>

            <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2.5 sm:gap-3 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setEnrollModalPatient(null)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer text-center"
              >
                {t("adminPatients.modal.cancel", "Cancel")}
              </button>
              <button
                type="submit"
                disabled={isSubmitting || programsLoading || !selectedProgramId}
                className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 cursor-pointer text-center"
              >
                {isSubmitting
                  ? t("adminPatients.modal.enrolling", "Enrolling...")
                  : t("adminPatients.modal.enrollBtn", "Grant Free Access")}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* 4. VIEW REGISTRATIONS / BOOKINGS HISTORY MODAL */}
      {bookingsModalPatient && (
        <Modal
          isOpen={true}
          onClose={() => setBookingsModalPatient(null)}
          title={t("adminPatients.modal.bookingsTitle", "Registered Programs & History")}
          subtitle={`Patient: ${bookingsModalPatient.fullName} (${bookingsModalPatient.email})`}
          size="2xl"
          footer={
            <>
              <button
                onClick={() => {
                  const p = bookingsModalPatient;
                  setBookingsModalPatient(null);
                  openEnrollModal(p);
                }}
                className="w-full sm:w-auto px-3.5 py-2 bg-[#B4804A] hover:bg-[#9d6c38] text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer text-center"
              >
                + Grant Another Free Access
              </button>
              <button
                onClick={() => setBookingsModalPatient(null)}
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-white cursor-pointer text-center"
              >
                {t("adminPatients.modal.close", "Close")}
              </button>
            </>
          }
        >
          {!bookingsModalPatient.bookings || bookingsModalPatient.bookings.length === 0 ? (
            <div className="text-center py-8 sm:py-10">
              <svg className="w-10 h-10 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              <p className="text-sm font-medium text-gray-500">
                {t("adminPatients.modal.noBookings", "This patient has not enrolled in any programs yet.")}
              </p>
              <button
                onClick={() => {
                  const p = bookingsModalPatient;
                  setBookingsModalPatient(null);
                  openEnrollModal(p);
                }}
                className="mt-3 px-4 py-1.5 bg-[#B4804A] text-white text-xs font-semibold rounded-lg hover:bg-[#9d6c38] transition-colors cursor-pointer"
              >
                + Grant First Free Access
              </button>
            </div>
          ) : (
            <div className="space-y-2.5 sm:space-y-3">
              {bookingsModalPatient.bookings.map((booking: PatientBooking, i: number) => {
                const title = booking.programTitle || booking.program?.title || "Program";
                const isFree = booking.isFree || booking.amount === 0 || booking.status === "free_access";
                return (
                  <div
                    key={booking.id || i}
                    className="p-3 sm:p-4 rounded-xl border border-gray-100 bg-gray-50/70 hover:bg-gray-50 flex flex-col sm:flex-row sm:items-center justify-between gap-3 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-[#041B44]/10 text-[#041B44] flex items-center justify-center font-bold text-xs shrink-0">
                        #{i + 1}
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-semibold text-gray-900 text-sm truncate">{title}</h4>
                        <div className="flex flex-wrap items-center gap-2 mt-0.5 text-xs text-gray-500">
                          <span>
                            Enrolled:{" "}
                            {booking.paymentDate
                              ? moment(booking.paymentDate).format("DD MMM, YYYY")
                              : "—"}
                          </span>
                          {booking.paymentMethod && <span>• {booking.paymentMethod}</span>}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 self-start sm:self-auto shrink-0">
                      {isFree ? (
                        <span className="px-2.5 py-1 bg-emerald-100 text-emerald-800 text-xs font-semibold rounded-full">
                          {t("adminPatients.modal.freeBadge", "Free Access")}
                        </span>
                      ) : (
                        <span className="px-2.5 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
                          {t("adminPatients.modal.paidBadge", "Paid")} ${booking.amount ?? 0}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
};

export default PatientsView;
